// AG Grid's model `filterType` discriminators.
const FilterModelType = {
  SET: "set",
  NUMBER: "number",
  TEXT: "text",
  DATE: "date",
  MULTI: "multi",
} as const;

const FILTER_COMPONENT_TO_MODEL_TYPE: Record<string, string> = {
  agSetColumnFilter: FilterModelType.SET,
  agNumberColumnFilter: FilterModelType.NUMBER,
  agTextColumnFilter: FilterModelType.TEXT,
  agDateColumnFilter: FilterModelType.DATE,
  agMultiColumnFilter: FilterModelType.MULTI,
};

// The model `filterType` a column expects, from its resolved `colDef.filter`.
// undefined for custom/default/unrecognised filters.
export function filterComponentToModelType(
  filter: unknown,
): string | undefined {
  return typeof filter === "string"
    ? FILTER_COMPONENT_TO_MODEL_TYPE[filter]
    : undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Saved views persist each column's filter model as it was at save time. When a
// column's filter component changes, the stale model can crash AG Grid on apply.
// Keep only a model matching the column's current filter (or unwrap a `multi`
// that wraps it); drop everything else so the column renders unfiltered.
export function reconcileFilterCondition(
  model: unknown,
  columnFilterType: string | undefined,
): unknown {
  // Unknown filter - can't judge the model, leave it untouched.
  if (!columnFilterType) {
    return model;
  }

  if (isPlainObject(model)) {
    const modelType = model.filterType;
    if (modelType === columnFilterType) {
      return model;
    }

    if (
      modelType === FilterModelType.MULTI &&
      Array.isArray(model.filterModels)
    ) {
      const inner = model.filterModels.find(
        nested =>
          isPlainObject(nested) && nested.filterType === columnFilterType,
      );
      return inner ?? null;
    }
  }

  return null;
}

// Reconciles every entry of a persisted filter model against the live column
// filter types. Returns null when nothing survives (AG Grid's "no filter").
export function reconcileFilterModel(
  filterModel: Record<string, unknown> | null | undefined,
  getColumnFilterType: (colId: string) => string | undefined,
): Record<string, unknown> | null {
  if (!filterModel) {
    return null;
  }

  const reconciled = Object.entries(filterModel).reduce<
    Record<string, unknown>
  >((acc, [colId, condition]) => {
    const next = reconcileFilterCondition(
      condition,
      getColumnFilterType(colId),
    );
    if (next != null) {
      acc[colId] = next;
    }
    return acc;
  }, {});

  return Object.keys(reconciled).length === 0 ? null : reconciled;
}
